import React from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import Circular from 'components/common/CustomCircular';
import { LanguageChip } from 'components/training';
import {
  QuestionDetailResult,
  QuestionListResult,
  QuestionSourceType,
  getQuestion,
  listQuestion,
  setQuestionHidden,
} from 'stores/TrainingAdminStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import QuestionCreateForm from './components/QuestionCreateForm';
import QuestionUpdateForm from './components/QuestionUpdateForm';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleTable,
  StatusBadge,
} from './components/console';

/**
 * 設問管理ページ（DEVELOPER）。
 * AWSコンソール風のリソーステーブル（タイトル / 方式 / 対応言語 / 状態）＋詳細ドロワー。
 */
const QuestionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [isProgress, setProgress] = React.useState(true);
  // 非表示切替などの操作中フラグ（サーキュラー表示・二重送信抑止）
  const [busy, setBusy] = React.useState(false);
  const [questions, setQuestions] = React.useState<QuestionListResult[]>([]);
  const [selected, setSelected] = React.useState<QuestionDetailResult | null>(null);
  const [drawerMode, setDrawerMode] = React.useState<'none' | 'create' | 'edit'>('none');
  const [search, setSearch] = React.useState('');
  const [includeHidden, setIncludeHidden] = React.useState(false);

  const fetchQuestions = React.useCallback(
    (params: { keyword?: string; includeHidden?: boolean }) => {
      setProgress(true);
      listQuestion(params)
        .then((result) => setQuestions(result))
        .catch(() => errorDefault('設問一覧の取得に失敗しました'))
        .finally(() => setProgress(false));
    },
    [],
  );

  React.useEffect(() => {
    fetchQuestions({ includeHidden });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeHidden]);

  // 現在適用中の検索条件（キーワード + 非表示含む）で再検索する
  const refetch = () => fetchQuestions({ keyword: search || undefined, includeHidden });

  const doSearch = () => {
    fetchQuestions({ keyword: search || undefined, includeHidden });
  };

  const handleRowClick = (q: QuestionListResult) => {
    setProgress(true);
    getQuestion(q.questionId)
      .then((detail) => {
        setSelected(detail);
        setDrawerMode('edit');
      })
      .catch(() => errorDefault('設問詳細の取得に失敗しました'))
      .finally(() => setProgress(false));
  };

  const handleCreated = () => {
    setDrawerMode('none');
    setSelected(null);
    refetch();
  };

  const reloadSelected = (questionId: string) => {
    getQuestion(questionId)
      .then((detail) => {
        setSelected(detail);
        setQuestions((prev) =>
          prev.map((q) =>
            q.questionId === detail.questionId
              ? {
                  ...q,
                  title: detail.title,
                  extensions: detail.answerCodes.map((a) => a.extension),
                }
              : q,
          ),
        );
      })
      .catch(() => errorDefault('設問詳細の再取得に失敗しました'));
  };

  const handleToggleHidden = async () => {
    if (!selected || busy) return;
    const next = !selected.isHidden;
    setBusy(true);
    try {
      await setQuestionHidden(selected.questionId, next);
      successDefault(next ? '設問を非表示にしました' : '設問を再表示しました');
      setSelected({ ...selected, isHidden: next });
      setQuestions((prev) =>
        prev.map((q) => (q.questionId === selected.questionId ? { ...q, isHidden: next } : q)),
      );
    } catch (e) {
      errorDefault('非表示切替に失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelected(null);
  };

  const columns: ConsoleColumn<QuestionListResult>[] = [
    {
      key: 'title',
      header: '設問タイトル',
      render: (q) => (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {q.isHidden && <VisibilityOffIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {q.title}
          </Box>
        </Stack>
      ),
    },
    {
      key: 'sourceType',
      header: '登録方式',
      width: 120,
      render: (q) => (
        <Chip
          size="small"
          label={QuestionSourceType[q.sourceType]}
          variant="outlined"
          sx={{ borderRadius: 1, height: 20, fontSize: 11 }}
        />
      ),
    },
    {
      key: 'extensions',
      header: '対応言語',
      width: 200,
      render: (q) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {q.extensions.length === 0 ? (
            <Box component="span" sx={{ color: 'text.disabled', fontSize: 12 }}>
              -
            </Box>
          ) : (
            q.extensions.map((ext) => <LanguageChip key={ext} extension={ext} />)
          )}
        </Stack>
      ),
    },
    {
      key: 'status',
      header: '状態',
      width: 90,
      render: (q) =>
        q.isHidden ? (
          <StatusBadge label="非表示" tone="muted" />
        ) : (
          <StatusBadge label="有効" tone="active" />
        ),
    },
  ];

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <Circular open={isProgress || busy} />
      <ConsoleHeader
        title="設問"
        count={questions.length}
        description="研修設問の登録・正解コード・期待値の管理（フォーム / knz）"
        backLabel="研修管理コンソール"
        onBack={() => navigate('/training-admin')}
        onRefresh={refetch}
        actions={
          <CustomButton
            label="設問を作成"
            buttonVariant="primary"
            icon={<AddIcon />}
            onClick={() => {
              setSelected(null);
              setDrawerMode('create');
            }}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={questions}
        rowKey={(q) => q.questionId}
        selectedKey={drawerMode === 'edit' ? selected?.questionId : undefined}
        onRowClick={handleRowClick}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchSubmit={doSearch}
        searchPlaceholder="タイトルで検索"
        emptyMessage="設問がありません。「設問を作成」から登録してください。"
        filterActions={
          <Button
            size="small"
            variant={includeHidden ? 'contained' : 'outlined'}
            onClick={() => setIncludeHidden((v) => !v)}
            startIcon={includeHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
            sx={{
              flexShrink: 0,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            非表示を含む
          </Button>
        }
      />

      {/* 作成ドロワー */}
      <ConsoleDetailDrawer open={drawerMode === 'create'} onClose={closeDrawer} title="設問の作成">
        <QuestionCreateForm onCreated={handleCreated} onCancel={closeDrawer} />
      </ConsoleDetailDrawer>

      {/* 編集ドロワー */}
      <ConsoleDetailDrawer
        open={drawerMode === 'edit' && selected !== null}
        onClose={closeDrawer}
        title={selected?.title ?? ''}
        badges={
          selected && (
            <>
              <Chip
                size="small"
                label={QuestionSourceType[selected.sourceType]}
                variant="outlined"
                sx={{ borderRadius: 1, height: 20, fontSize: 11 }}
              />
              {selected.isHidden && <StatusBadge label="非表示" tone="muted" />}
            </>
          )
        }
        headerActions={
          selected && (
            <Button
              variant="outlined"
              size="small"
              color={selected.isHidden ? 'primary' : 'warning'}
              startIcon={selected.isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              onClick={handleToggleHidden}
              disabled={busy}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
              }}
            >
              {selected.isHidden ? '再表示' : '非表示にする'}
            </Button>
          )
        }
      >
        {selected && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 2,
              p: 1.25,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {selected.isHidden ? 'この設問は非表示です' : 'この設問は有効です'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                非表示にすると課題への追加候補・一覧の既定から除外されます（追加済みには影響しません）
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              color={selected.isHidden ? 'primary' : 'warning'}
              startIcon={selected.isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              onClick={handleToggleHidden}
              disabled={busy}
              sx={{
                flexShrink: 0,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
              }}
            >
              {selected.isHidden ? '再表示' : '非表示にする'}
            </Button>
          </Box>
        )}
        {selected && (
          <QuestionUpdateForm
            question={selected}
            onUpdated={() => reloadSelected(selected.questionId)}
          />
        )}
      </ConsoleDetailDrawer>
    </Box>
  );
};

export default QuestionManagement;
